import React, { FC, useState, useEffect } from 'react'
import ExcelJS from 'exceljs'
import classnames from 'classnames'
import { ConsolidatedHighlightsProps } from './ConsolidatedHighlights.types'
import './ConsolidatedHighlights.scss'
import { sanitizeHTML, APIENDPOINT, getCWPEnvironmentForURL } from '../../utils/common-utils'

export const ConsolidatedHighlights: FC<ConsolidatedHighlightsProps> = props => {
  const [sheetData, setSheetData] = useState<{ [key: string]: Array<string[]> }>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [apiError, setApiError] = useState<boolean>(false)

  const env = getCWPEnvironmentForURL(window.location.href)

  const { content } = props
  const {
    title,
    introduction,
    description,
    disclaimer,
    excelFilePath,
    bgColorClass,
    textColorClass,
    backgroundImage,
    showOverlay,
  } = content || {}

  const showOverlayAndFacet = showOverlay === 'true'

  const isRichValue = (cellValue: ExcelJS.CellRichTextValue) => {
    return Boolean(cellValue && Array.isArray(cellValue.richText))
  }

  const richToHtml = (cellValue: ExcelJS.CellRichTextValue) => {
    const str = cellValue.richText
      .map(({ font = {}, text }) => {
        return [
          font.vertAlign === 'superscript' ? '<sup>' : '',
          font.vertAlign === 'subscript' ? '<sub>' : '',
          text,
          font.vertAlign === 'subscript' ? '</sub>' : '',
          font.vertAlign === 'superscript' ? '</sup>' : '',
        ].join('')
      })
      .join('')
    return str
  }

  useEffect(() => {
    const loadHighlightsWorkbook = async () => {
      setLoading(true)
      setApiError(false)
      const response = await fetch(`${APIENDPOINT(env).baseGallery}${excelFilePath}`)
      setLoading(false)
      const status = response.status
      if (status < 200 || status > 299) {
        setApiError(true)
        throw Error('Error response: ' + status)
      }

      const readBuffer = await response.arrayBuffer()
      const workbook = new ExcelJS.Workbook()
      return workbook.xlsx.load(readBuffer)
    }

    const setHighlightsData = async () => {
      try {
        const workbook = await loadHighlightsWorkbook()
        //Parsing sheets data
        const data: { [key: string]: string[][] } = {}
        workbook.eachSheet(worksheet => {
          const sheetName = worksheet.name
          const sheetRows: string[][] = []

          worksheet.eachRow(row => {
            const rowData = [...(row.values as ExcelJS.CellValue[])].map(cell => {
              return cell && isRichValue(cell as ExcelJS.CellRichTextValue)
                ? richToHtml(cell as ExcelJS.CellRichTextValue)
                : cell
            })
            sheetRows.push(rowData.slice(1) as string[]) //since indexing in xlsx start from 1 hence 0th element will always be empty.
          })
          data[sheetName] = sheetRows
        })
        setSheetData(data)
      } catch {
        console.log('Error in Fetching')
      }
    }
    setHighlightsData()
  }, [])

  const customWidthForTable = (ind: number) => {
    if (ind == 0 || ind == 1) {
      return 'u-paddingRight48'
    }
    return 'u-paddingRight0'
  }

  return (
    <>
      <style
        type='text/css'
        dangerouslySetInnerHTML={{
          __html: `
        .consolitated-high-background { background: url("${backgroundImage}");
        background-size: cover;
        background-repeat: no-repeat;
        width: 100%;
        position:relative;
        box-sizing: border-box; }
      `,
        }}
      />

      <div className='u-adel-reset' id='highlight'>
        <div className='consolidated-highlights Content'>
          <section className={`${showOverlayAndFacet ? 'consolitated-high-background' : ''}`}>
            <div
              className={`u-paddingVert80 ${
                showOverlayAndFacet ? 'u-sizeConstrained' : bgColorClass + ' u-brandExtension'
              } ${textColorClass}`}>
              <div className={`${showOverlayAndFacet ? 'highlight-overlay' : ''}`}>
                <div
                  className={`${
                    showOverlayAndFacet
                      ? 'highlights-container-overlay'
                      : 'highlights-container u-marginVert32 '
                  } u-sizeConstrained`}>
                  <div className='highlights-table-title Content u-marginBottom80 u-sm-marginBottom40'>
                    <h2
                      className={`${
                        showOverlayAndFacet ? ' ' : 'u-headline u-textSecondary'
                      } u-textSizeMsrp7 u-md-textSizeMsrp6 u-sm-textSizeMsrp6 u-marginBottom8 ${textColorClass}`}>
                      {title}
                    </h2>
                    <div
                      className={`highlight-intro adel-override u-textColorLightMint u-marginBottom20 u-subHeadline ${
                        showOverlayAndFacet ? ' ' : 'u-textItalic'
                      } u-textSecondary u-textSizeMsrp6 u-md-textSizeMsrp4 u-sm-textSizeMsrp4 ${textColorClass}`}>
                      <h3>{introduction}</h3>
                    </div>
                    {description ? (
                      <div
                        className={`adel-override u-textSecondary ${textColorClass}`}
                        dangerouslySetInnerHTML={{
                          __html: description,
                        }}
                      />
                    ) : null}
                  </div>
                  {loading ? (
                    <div className='u-flex u-flexJustifyCenter'>
                      <div className={`Loading ${textColorClass}`}>
                        <div className='Loading-indicator'></div>
                        <div className='Loading-indicator'></div>
                        <div className='Loading-indicator'></div>
                      </div>
                    </div>
                  ) : null}
                  {!loading && Object.keys(sheetData).length ? (
                    <div
                      className='highlights-tables-container u-marginBottom48'
                      style={
                        {
                          '--rows': Math.ceil(Object.keys(sheetData).length / 2),
                        } as React.CSSProperties
                      }>
                      {Object.keys(sheetData).map((sheetName, index) => (
                        <div
                          key={sheetName}
                          className={`highlights-table ${
                            showOverlayAndFacet ? customWidthForTable(index) : 'u-paddingRight20'
                          } u-md-paddingRight0 u-sm-paddingRight0 u-md-marginBottom40 u-sm-marginBottom40`}>
                          <table>
                            <thead>
                              <tr>
                                {sheetData[sheetName][0].map((cell, cellIndex) => (
                                  <th
                                    key={`${cell}`}
                                    className={classnames({
                                      'u-textBold u-textLeft': cellIndex === 0,
                                      'u-tableBorder u-textBold u-textColorLightMint':
                                        cellIndex === 1,
                                    })}
                                    dangerouslySetInnerHTML={{
                                      __html: sanitizeHTML(cell) || '',
                                    }}
                                  />
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {sheetData[sheetName].slice(1).map((row, rowIndex) => (
                                <tr key={`${sheetName}`} className='highlights-table-row-border'>
                                  {row.map((cell, cellIndex) => (
                                    <>
                                      {cellIndex === 0 ? (
                                        <th
                                          scope='row'
                                          className='u-textNormal u-textLeft'
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeHTML(cell) || '',
                                          }}
                                        />
                                      ) : (
                                        <td
                                          className={classnames({
                                            'u-tableBorder u-textBold u-textColorLightMint':
                                              cellIndex === 1,
                                          })}
                                          dangerouslySetInnerHTML={{
                                            __html: sanitizeHTML(cell) || '',
                                          }}
                                        />
                                      )}
                                    </>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {!loading && apiError ? (
                    <div className='State--error'>
                      <div className='State-content'>
                        <p className={`u-textSecondary ${textColorClass}`}>
                          We are experiencing some difficulties fetching highlights. Please try
                          again later.
                        </p>
                      </div>
                    </div>
                  ) : null}
                  {disclaimer && (
                    <div
                      className={`highlights-table-disclosure Content Content--small Content--reversed u-marginBottom8 ${textColorClass}`}
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(disclaimer),
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  )
}
